import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { matchPassioRoute } from "@passio/api-client";
import type { ModelPickKind, PublishedPlatformModels, PublishedRoute, TestedModel } from "@passio/easyai-client";
import { easyai } from "./clients";

export const PICK_KINDS: ModelPickKind[] = ["recommended", "quality", "budget"];

export const PICKS: Record<ModelPickKind, { label: string; color: string; summary: string }> = {
  recommended: { label: "Recommended", color: "lime", summary: "Accurate enough for nearly every call, and the best balance of speed and cost." },
  quality: { label: "High quality", color: "violet", summary: "The most accurate. Slower and costs more." },
  budget: { label: "Budget", color: "teal", summary: "The cheapest and quickest that still does the job. Can miss more." },
};

/** Published test results keyed by demo route id (matched on the API path). Routes the demo doesn't call are dropped. */
export function testsByRoute(published?: PublishedPlatformModels): Map<string, PublishedRoute> {
  const out = new Map<string, PublishedRoute>();
  for (const r of published?.routes ?? []) {
    for (const path of r.api_paths ?? []) {
      const route = matchPassioRoute("POST", path);
      if (route?.ai && !out.has(route.id)) out.set(route.id, r);
    }
  }
  return out;
}

/** The picks a model holds on a route, e.g. ["recommended"]. */
export const picksFor = (tests: PublishedRoute | undefined, tag: string): ModelPickKind[] => PICK_KINDS.filter((k) => tests?.picks?.[k]?.model === tag);

export const testedModel = (tests: PublishedRoute | undefined, tag: string): TestedModel | undefined => tests?.models?.find((m) => m.model === tag);

export const fmtAccuracy = (v: number | null | undefined) => (v == null ? null : `${Math.round(v)}%`);
export const fmtSeconds = (v: number | null | undefined) => (v == null ? null : v < 10 ? `${v.toFixed(1)} s` : `${Math.round(v)} s`);

/** Tested model picks for the routes Passio has published. Empty (not an error) when none are published or EasyAI is unavailable. */
export function useRouteTests() {
  const q = useQuery({ queryKey: ["route-tests"], queryFn: () => easyai.publishedPlatformModels(), staleTime: 10 * 60_000, retry: 1 });
  const byRoute = useMemo(() => testsByRoute(q.data), [q.data]);
  return { byRoute, isPending: q.isPending };
}
